import { useEffect, useState } from 'react';
import { getPolizas, getPolizasPorVencer, createPoliza, getClientes, getAseguradoras, getRamos } from '../services/api';
import Modal from '../components/ui/Modal';

const ESTATUS_COLORS: Record<string, string> = {
  Activa:        'bg-green-100 text-green-700',
  PorVencer:     'bg-yellow-100 text-yellow-700',
  Vencida:       'bg-red-100 text-red-700',
  Cancelada:     'bg-gray-100 text-gray-500',
  EnRenovacion:  'bg-blue-100 text-blue-700',
};

const FRECUENCIAS = ['Mensual', 'Trimestral', 'Semestral', 'Anual'];

const EMPTY_FORM = {
  numeroPoliza: '', ramo: '', aseguradora: '', prima: '',
  frecuenciaPago: 'Anual', sumaAsegurada: '', vigenciaInicio: '',
  vigenciaFin: '', clienteId: '', notas: '',
};

export default function Polizas() {
  const [polizas, setPolizas] = useState<any[]>([]);
  const [tab, setTab] = useState<'todas' | 'por-vencer'>('todas');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [clientes, setClientes] = useState<any[]>([]);
  const [aseguradoras, setAseguradoras] = useState<any[]>([]);
  const [ramos, setRamos] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = () => {
    if (tab === 'todas') getPolizas().then(res => setPolizas(res.data));
    else getPolizasPorVencer(30).then(res => setPolizas(res.data));
  };

  useEffect(() => { load(); }, [tab]);

  useEffect(() => {
    getClientes().then(res => setClientes(res.data));
    getAseguradoras().then(res => setAseguradoras(res.data));
    getRamos().then(res => setRamos(res.data));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await createPoliza({
        numeroPoliza: form.numeroPoliza,
        ramo: form.ramo,
        aseguradora: form.aseguradora,
        prima: Number(form.prima),
        frecuenciaPago: form.frecuenciaPago,
        sumaAsegurada: form.sumaAsegurada ? Number(form.sumaAsegurada) : null,
        vigenciaInicio: form.vigenciaInicio,
        vigenciaFin: form.vigenciaFin,
        clienteId: Number(form.clienteId),
        notas: form.notas || null,
      });
      setShowModal(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al guardar. Verifica los datos.');
    } finally {
      setSaving(false);
    }
  };

  const field = (key: keyof typeof EMPTY_FORM) => ({
    value: form[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(f => ({ ...f, [key]: e.target.value })),
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Pólizas</h2>
        <button
          onClick={() => { setShowModal(true); setForm(EMPTY_FORM); setError(''); }}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-primary-700"
        >
          + Nueva póliza
        </button>
      </div>

      <div className="flex gap-2 mb-4">
        {(['todas', 'por-vencer'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              tab === t ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 border'
            }`}
          >
            {t === 'todas' ? 'Todas' : 'Por vencer (30 días)'}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
            <tr>
              <th className="px-4 py-3 text-left">No. Póliza</th>
              <th className="px-4 py-3 text-left">Cliente</th>
              <th className="px-4 py-3 text-left">Ramo</th>
              <th className="px-4 py-3 text-left">Aseguradora</th>
              <th className="px-4 py-3 text-left">Prima</th>
              <th className="px-4 py-3 text-left">Vigencia fin</th>
              <th className="px-4 py-3 text-left">Estatus</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {polizas.map(p => (
              <tr key={p.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-800">{p.numeroPoliza}</td>
                <td className="px-4 py-3 text-gray-600">{p.cliente?.nombre}</td>
                <td className="px-4 py-3 text-gray-600">{p.ramo}</td>
                <td className="px-4 py-3 text-gray-600">{p.aseguradora}</td>
                <td className="px-4 py-3 text-gray-600">${p.prima.toLocaleString()}</td>
                <td className="px-4 py-3 text-gray-600">
                  {new Date(p.vigenciaFin).toLocaleDateString('es-MX')}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${ESTATUS_COLORS[p.estatus]}`}>
                    {p.estatus}
                  </span>
                </td>
              </tr>
            ))}
            {polizas.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                  No hay pólizas registradas
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <Modal title="Nueva póliza" onClose={() => setShowModal(false)} size="lg">
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            <div className="col-span-2 text-xs font-semibold text-gray-400 uppercase tracking-wider pt-1">
              Datos de la póliza
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Número de póliza *</label>
              <input type="text" {...field('numeroPoliza')} required
                placeholder="GNP-2024-00001"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cliente *</label>
              <select {...field('clienteId')} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Seleccionar cliente...</option>
                {clientes.map((c: any) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ramo *</label>
              <select {...field('ramo')} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Seleccionar ramo...</option>
                {ramos.map((r: any) => <option key={r.id} value={r.nombre}>{r.icono} {r.nombre}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Aseguradora *</label>
              <select {...field('aseguradora')} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Seleccionar aseguradora...</option>
                {aseguradoras.map((a: any) => <option key={a.id} value={a.nombre}>{a.nombre}</option>)}
              </select>
            </div>

            <div className="col-span-2 text-xs font-semibold text-gray-400 uppercase tracking-wider pt-2">
              Prima y vigencia
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prima ($) *</label>
              <input type="number" {...field('prima')} required min={1} step={0.01}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Frecuencia de pago</label>
              <select {...field('frecuenciaPago')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                {FRECUENCIAS.map(f => <option key={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Suma asegurada ($)</label>
              <input type="number" {...field('sumaAsegurada')} min={0} step={1000}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vigencia inicio *</label>
              <input type="date" {...field('vigenciaInicio')} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vigencia fin *</label>
              <input type="date" {...field('vigenciaFin')} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
              <textarea {...field('notas')} rows={2} placeholder="Coberturas especiales, observaciones..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
            </div>

            {error && <p className="col-span-2 text-red-500 text-sm">{error}</p>}

            <div className="col-span-2 flex gap-2 justify-end pt-2 border-t border-gray-100">
              <button type="button" onClick={() => setShowModal(false)}
                className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                Cancelar
              </button>
              <button type="submit" disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-60">
                {saving ? 'Guardando...' : 'Guardar póliza'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
